import { useMemo } from 'react'

import {
  filterOptionsByProject,
  withDynamicPropertyOptions,
} from '@/hooks/gridOptionLookups'
import { useInventoryProducts } from '@/services/product'
import { adaptCatalogProductStatuses } from '@/features/property/legacy/catalogEditor.productAdapter'
import { useCatalog, useCatalogs } from '@/services/queries/catalog'
import { useOptions } from '@/hooks'
import {
  catalogLocaleToBusinessLanguage,
  LEGACY_BASE_LANGUAGE,
} from '@/utils/languagePolicy'

interface UseCatalogDataProps {
  projectID: string
  selectedCatalogId?: string | null
  contentLocale?: string
}

export const useCatalogData = ({
  projectID,
  selectedCatalogId,
  contentLocale,
}: UseCatalogDataProps) => {
  const context = useOptions({
    propertyOptionsLang: catalogLocaleToBusinessLanguage(contentLocale),
  })
  const structureContext = useOptions({
    propertyOptionsLang: LEGACY_BASE_LANGUAGE,
  })

  // Create a local context with filtered shapeID without mutating the original
  const catalogContext = useMemo(() => {
    if (!context) return undefined
    return withDynamicPropertyOptions(
      context,
      'shapeID',
      filterOptionsByProject(context.propertyOptions?.shapeID, projectID),
    )
  }, [context, projectID])
  const catalogStructureContext = useMemo(() => {
    if (!structureContext) return undefined
    return withDynamicPropertyOptions(
      structureContext,
      'shapeID',
      filterOptionsByProject(
        structureContext.propertyOptions?.shapeID,
        projectID,
      ),
    )
  }, [projectID, structureContext])

  // Fetch products
  const {
    products: rawProducts = [],
    hasNextPage,
    isError: productsError,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
    hasInitialPageLoaded,
  } = useInventoryProducts({
    filters: {
      projectID: { eq: projectID },
      progress: { ne: 'done' },
      type: { ne: 'done' },
      nature: { ne: 'service' },
    },
  })

  const products = useMemo(
    () => adaptCatalogProductStatuses(rawProducts, catalogContext ?? {}),
    [catalogContext, rawProducts],
  )

  const initialProductsLoaded = hasInitialPageLoaded && !productsError
  const allProductsLoaded =
    !isLoadingProducts && !isFetchingProducts && !productsError && !hasNextPage

  // Fetch catalogs
  const {
    data: catalogs = [],
    isSuccess: hasFetchCatalogs,
    isError: catalogsError,
    isLoading: isLoadingCatalogs,
  } = useCatalogs({
    projectID,
  })

  const effectiveSelectedCatalogId = useMemo(() => {
    if (selectedCatalogId === null) return undefined
    if (selectedCatalogId) return selectedCatalogId
    return catalogs[0]?.id
  }, [catalogs, selectedCatalogId])

  const {
    data: selectedCatalog,
    isLoading: isLoadingSelectedCatalog,
    isFetching: isFetchingSelectedCatalog,
  } = useCatalog(effectiveSelectedCatalogId)

  return {
    products,
    initialProductsLoaded,
    allProductsLoaded,
    productsError,
    isLoadingProducts,
    isFetchingProducts,
    catalogs,
    selectedCatalog,
    selectedCatalogId: effectiveSelectedCatalogId,
    hasFetchCatalogs,
    catalogsError,
    isLoadingCatalogs,
    isLoadingSelectedCatalog,
    isFetchingSelectedCatalog,
    isHydratingProducts: initialProductsLoaded && !allProductsLoaded,
    context,
    catalogContext,
    catalogStructureContext,
  }
}
